import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeyTransportApi } from '../../api/journeyTransportApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { Button, ConfirmDialog, EmptyState, ErrorState, LoadingState } from '../../components/ui';
import {
  fillWorkspaceProductChrome,
  logisticsTransportModeProductLabel,
  WORKSPACE_PRODUCT_CHROME,
} from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  buildTransportWritePayload,
  classifyLogisticsError,
  draftFromTransportDetail,
  emptyTransportDraft,
  isSuggestedTransportMode,
  normalizeTransportDetail,
  normalizeTransportSummary,
  TRANSPORT_MODE_SUGGESTIONS,
  validateTransportDraft,
} from './transportModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';

const WRITE_ROLES = new Set(['OWNER', 'COORDINATOR', 'MEMBER']);
const REMOVE_ROLES = new Set(['OWNER', 'COORDINATOR']);
const WRITABLE_LIFECYCLES = new Set(['DRAFT', 'READY', 'TRAVELING']);

function transportModeDataAttr(mode) {
  const raw = String(mode || '').trim().toUpperCase();
  return raw || 'UNSPECIFIED';
}

function beginCreateMode(setMode, setEditingId, setDraft, setFormError) {
  setMode('create');
  setEditingId(null);
  setDraft(emptyTransportDraft());
  setFormError('');
}

export default function JourneyTransportPage() {
  const { journeyId } = useParams();
  const { t } = useTravelTranslation();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [mode, setMode] = useState('list');
  const [draft, setDraft] = useState(emptyTransportDraft());
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setErrorModel(null);
    setFormError('');
    setConfirmDelete(null);
    try {
      const [detailPayload, listPayload] = await Promise.all([
        journeyApi.getDetail(journeyId),
        journeyTransportApi.list(journeyId),
      ]);
      if (requestId !== generation.current) return;
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      const rows = Array.isArray(listPayload) ? listPayload : [];
      setItems(rows.map(normalizeTransportSummary));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyLogisticsError(cause);
      if (kind === 'not-found' || classifyJourneyAccessError(cause) === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || classifyJourneyAccessError(cause) === 'forbidden') setPhase('forbidden');
      else {
        setErrorModel(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
      }
    }
  }, [journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  const canWrite = WRITABLE_LIFECYCLES.has(journey?.lifecycleStatus)
    && WRITE_ROLES.has(journey?.requesterRole);
  const canRemove = WRITABLE_LIFECYCLES.has(journey?.lifecycleStatus)
    && REMOVE_ROLES.has(journey?.requesterRole);

  async function runMutation(work) {
    if (mutating.current) return;
    mutating.current = true;
    setBusy(true);
    setFormError('');
    try {
      await work();
      setMode('list');
      setEditingId(null);
      setDraft(emptyTransportDraft());
      setConfirmDelete(null);
      await load();
    } catch (cause) {
      const kind = classifyLogisticsError(cause);
      if (kind === 'validation') setFormError(WORKSPACE_PRODUCT_CHROME.formCheckFallback);
      else if (kind === 'forbidden') setFormError(WORKSPACE_PRODUCT_CHROME.permissionDeniedChange);
      else {
        setFormError(
          resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed')
            || WORKSPACE_PRODUCT_CHROME.transportChangeFailed,
        );
      }
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  async function submitForm(event) {
    event.preventDefault();
    const validation = validateTransportDraft(draft);
    if (validation) {
      setFormError(validation);
      return;
    }
    const body = buildTransportWritePayload(draft);
    await runMutation(async () => {
      if (mode === 'edit' && editingId) {
        await journeyTransportApi.update(journey.journeyId, editingId, body);
      } else {
        await journeyTransportApi.create(journey.journeyId, body);
      }
    });
  }

  async function beginEdit(item) {
    setFormError('');
    setMode('edit');
    setEditingId(item.transportId);
    setDraft(emptyTransportDraft());
    try {
      const detail = normalizeTransportDetail(
        await journeyTransportApi.get(journey.journeyId, item.transportId),
      );
      setDraft(draftFromTransportDetail(detail));
    } catch (cause) {
      setFormError(
        resolveSafeUserVisibleError(cause, t, 'travel.common.loadFailed')
          || WORKSPACE_PRODUCT_CHROME.transportLoadDetailFailed,
      );
    }
  }

  async function confirmRemove(item) {
    await runMutation(async () => {
      await journeyTransportApi.remove(journey.journeyId, item.transportId, { confirm: true });
    });
  }

  function openCreate() {
    beginCreateMode(setMode, setEditingId, setDraft, setFormError);
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state" aria-live="polite">
        <LoadingState label={t('travel.common.loading')} data-testid="transport-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state" data-testid="transport-not-found">
        <EmptyState
          title={WORKSPACE_PRODUCT_CHROME.notFoundTitle}
          description={WORKSPACE_PRODUCT_CHROME.notFoundBody}
          action={<Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>}
        />
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state" data-testid="transport-forbidden">
        <EmptyState
          title={WORKSPACE_PRODUCT_CHROME.forbiddenTitle}
          description={WORKSPACE_PRODUCT_CHROME.forbiddenBody}
          action={<Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>}
        />
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = errorModel ? resolveUiErrorCopy(errorModel, t) : null;
    return (
      <main className="journey-workspace-state" data-testid="transport-error">
        <ErrorState
          title={errorCopy?.title || t('travel.common.apiError.unknown.title')}
          description={errorCopy?.description || t('travel.common.apiError.unknown.description')}
          retryable={Boolean(errorCopy?.retryable)}
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  const showToolbarAdd = canWrite && (items.length > 0 || mode !== 'list');

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey.title}</h1>
          <dl className="journey-workspace__facts">
            {journey.destination ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.destination}</dt>
                <dd>{journey.destination}</dd>
              </div>
            ) : null}
            {journey.dateRange ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.dates}</dt>
                <dd>{journey.dateRange}</dd>
              </div>
            ) : null}
            <div>
              <dt>{WORKSPACE_PRODUCT_CHROME.status}</dt>
              <dd>{journey.lifecycleLabel}</dd>
            </div>
            {journey.requesterRoleLabel ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.yourRole}</dt>
                <dd>{journey.requesterRoleLabel}</dd>
              </div>
            ) : null}
          </dl>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel" aria-labelledby="transport-heading">
        <div className="journey-workspace__panel-head">
          <div>
            <h2 id="transport-heading">{WORKSPACE_PRODUCT_CHROME.transport}</h2>
            <p className="journey-logistics__panel-lead">{WORKSPACE_PRODUCT_CHROME.transportPanelLead}</p>
          </div>
          <div className="journey-logistics__toolbar">
            <Button type="button" variant="secondary" onClick={load} disabled={busy}>
              {WORKSPACE_PRODUCT_CHROME.refresh}
            </Button>
            {showToolbarAdd ? (
              <Button type="button" variant="primary" disabled={busy} onClick={openCreate}>
                {WORKSPACE_PRODUCT_CHROME.addTransport}
              </Button>
            ) : null}
          </div>
        </div>

        {formError ? <p className="journey-workspace__error" role="alert">{formError}</p> : null}

        {mode === 'create' || mode === 'edit' ? (
          <TransportForm
            draft={draft}
            setDraft={setDraft}
            onSubmit={submitForm}
            onCancel={() => { setMode('list'); setEditingId(null); setFormError(''); }}
            busy={busy}
            submitLabel={mode === 'edit'
              ? WORKSPACE_PRODUCT_CHROME.saveTransport
              : WORKSPACE_PRODUCT_CHROME.createTransport}
          />
        ) : null}

        {items.length === 0 && mode === 'list' ? (
          <EmptyState
            className="journey-logistics__empty"
            data-testid="transport-empty"
            title={WORKSPACE_PRODUCT_CHROME.transportEmptyTitle}
            description={WORKSPACE_PRODUCT_CHROME.transportEmptyDescription}
            action={
              canWrite ? (
                <Button type="button" variant="secondary" disabled={busy} onClick={openCreate}>
                  {WORKSPACE_PRODUCT_CHROME.addTransport}
                </Button>
              ) : null
            }
          />
        ) : items.length === 0 ? null : (
          <ul className="journey-logistics__list">
            {items.map((item) => {
              const modeLabel = logisticsTransportModeProductLabel(item.transportMode);
              const hasRoute = Boolean(item.sourceLabel || item.destinationLabel);
              const hasTimes = Boolean(item.departureAt || item.arrivalAt);
              return (
                <li key={item.transportId} className="journey-logistics__item">
                  <div className="journey-logistics__item-main">
                    <div className="journey-logistics__item-title-row">
                      <span
                        className="journey-logistics__mode-badge"
                        data-mode={transportModeDataAttr(item.transportMode)}
                      >
                        {modeLabel}
                      </span>
                      {item.carrierText ? <strong>{item.carrierText}</strong> : null}
                    </div>
                    <dl className="journey-logistics__meta">
                      <div className="journey-logistics__meta-row">
                        <dt>{WORKSPACE_PRODUCT_CHROME.transportDepartureLocation}</dt>
                        <dd>
                          {hasRoute ? (
                            <span className="journey-logistics__route">
                              <span className="journey-logistics__route-point">
                                {item.sourceLabel || '—'}
                              </span>
                              <span className="journey-logistics__route-arrow" aria-hidden="true">
                                {WORKSPACE_PRODUCT_CHROME.transportRouteSeparator}
                              </span>
                              <span className="journey-logistics__route-point">
                                {item.destinationLabel || '—'}
                              </span>
                            </span>
                          ) : WORKSPACE_PRODUCT_CHROME.transportRouteUnset}
                        </dd>
                      </div>
                      <div className="journey-logistics__meta-row">
                        <dt>{WORKSPACE_PRODUCT_CHROME.transportDeparture}</dt>
                        <dd>
                          {item.departureAt
                            ? formatDateTime(item.departureAt)
                            : WORKSPACE_PRODUCT_CHROME.transportTimesNotSet}
                        </dd>
                      </div>
                      <div className="journey-logistics__meta-row">
                        <dt>{WORKSPACE_PRODUCT_CHROME.transportArrival}</dt>
                        <dd>
                          {item.arrivalAt
                            ? formatDateTime(item.arrivalAt)
                            : (hasTimes ? '—' : WORKSPACE_PRODUCT_CHROME.transportTimesNotSet)}
                        </dd>
                      </div>
                      {item.bookingReference ? (
                        <div className="journey-logistics__meta-row">
                          <dt>{WORKSPACE_PRODUCT_CHROME.referencePrefix}</dt>
                          <dd>{item.bookingReference}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                  <div className="journey-logistics__item-actions">
                    {canWrite ? (
                      <Button type="button" variant="secondary" disabled={busy} onClick={() => beginEdit(item)}>
                        {WORKSPACE_PRODUCT_CHROME.edit}
                      </Button>
                    ) : null}
                    {canRemove ? (
                      <Button type="button" variant="danger" disabled={busy} onClick={() => setConfirmDelete(item)}>
                        {WORKSPACE_PRODUCT_CHROME.delete}
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={WORKSPACE_PRODUCT_CHROME.transportDeleteTitle}
        description={
          confirmDelete
            ? fillWorkspaceProductChrome(WORKSPACE_PRODUCT_CHROME.transportDeleteBody, {
              name: logisticsTransportModeProductLabel(confirmDelete.transportMode)
                || confirmDelete.transportModeLabel,
            })
            : ''
        }
        confirmLabel={busy ? WORKSPACE_PRODUCT_CHROME.deleting : WORKSPACE_PRODUCT_CHROME.delete}
        cancelLabel={WORKSPACE_PRODUCT_CHROME.cancel}
        danger
        loading={busy}
        onConfirm={() => confirmDelete && confirmRemove(confirmDelete)}
        onCancel={() => { if (!busy) setConfirmDelete(null); }}
      />
    </main>
  );
}

function TransportForm({ draft, setDraft, onSubmit, onCancel, busy, submitLabel }) {
  const modeId = useId();
  const sourceId = useId();
  const destinationId = useId();
  const departureId = useId();
  const arrivalId = useId();
  const carrierId = useId();
  const refId = useId();
  const noteId = useId();
  const unknownMode = draft.transportMode && !isSuggestedTransportMode(draft.transportMode);

  return (
    <form className="journey-logistics__form" onSubmit={onSubmit} noValidate>
      <label htmlFor={modeId}>
        {WORKSPACE_PRODUCT_CHROME.transportMode}
        <select
          id={modeId}
          value={draft.transportMode}
          onChange={(e) => setDraft((c) => ({ ...c, transportMode: e.target.value }))}
        >
          <option value="">{WORKSPACE_PRODUCT_CHROME.transportModeUnspecified}</option>
          {TRANSPORT_MODE_SUGGESTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {logisticsTransportModeProductLabel(option.value)}
            </option>
          ))}
          {unknownMode ? (
            <option value={draft.transportMode}>{draft.transportMode}</option>
          ) : null}
        </select>
      </label>
      <label htmlFor={sourceId}>
        {WORKSPACE_PRODUCT_CHROME.transportDepartureLocation}
        <input
          id={sourceId}
          value={draft.source}
          onChange={(e) => setDraft((c) => ({ ...c, source: e.target.value }))}
          placeholder={WORKSPACE_PRODUCT_CHROME.optionalSummaryPlaceholder}
        />
      </label>
      <label htmlFor={destinationId}>
        {WORKSPACE_PRODUCT_CHROME.transportArrivalLocation}
        <input
          id={destinationId}
          value={draft.destination}
          onChange={(e) => setDraft((c) => ({ ...c, destination: e.target.value }))}
          placeholder={WORKSPACE_PRODUCT_CHROME.optionalSummaryPlaceholder}
        />
      </label>
      <div className="journey-workspace__dates">
        <label htmlFor={departureId}>
          {WORKSPACE_PRODUCT_CHROME.transportDeparture}
          <input
            id={departureId}
            type="datetime-local"
            value={draft.departureAt}
            onChange={(e) => setDraft((c) => ({ ...c, departureAt: e.target.value }))}
          />
        </label>
        <label htmlFor={arrivalId}>
          {WORKSPACE_PRODUCT_CHROME.transportArrival}
          <input
            id={arrivalId}
            type="datetime-local"
            value={draft.arrivalAt}
            onChange={(e) => setDraft((c) => ({ ...c, arrivalAt: e.target.value }))}
          />
        </label>
      </div>
      <label htmlFor={carrierId}>
        {WORKSPACE_PRODUCT_CHROME.transportCarrier}
        <input
          id={carrierId}
          value={draft.carrierText}
          maxLength={200}
          onChange={(e) => setDraft((c) => ({ ...c, carrierText: e.target.value }))}
        />
      </label>
      <label htmlFor={refId}>
        {WORKSPACE_PRODUCT_CHROME.transportBookingReference}
        <input
          id={refId}
          value={draft.bookingReference}
          maxLength={128}
          onChange={(e) => setDraft((c) => ({ ...c, bookingReference: e.target.value }))}
        />
      </label>
      <label htmlFor={noteId}>
        {WORKSPACE_PRODUCT_CHROME.transportNote}
        <textarea
          id={noteId}
          rows={3}
          value={draft.note}
          onChange={(e) => setDraft((c) => ({ ...c, note: e.target.value }))}
        />
      </label>
      <div className="journey-workspace__edit-actions">
        <Button type="submit" variant="primary" loading={busy}>
          {busy ? WORKSPACE_PRODUCT_CHROME.saving : submitLabel}
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={onCancel}>
          {WORKSPACE_PRODUCT_CHROME.cancel}
        </Button>
      </div>
    </form>
  );
}
